import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { X, Plus, Minus, ShoppingBag, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
    const { items, total, count, open, setOpen, updateQty, removeItem, clear } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [checkingOut, setCheckingOut] = useState(false);
    const [step, setStep] = useState("cart"); // cart | checkout
    const [address, setAddress] = useState("");
    const [phone, setPhone] = useState("");
    const [notes, setNotes] = useState("");

    if (!open) return null;

    const placeOrder = async () => {
        if (!user) {
            toast.error("Please sign in to place your order");
            setOpen(false);
            navigate("/login");
            return;
        }
        if (!address.trim() || !phone.trim()) {
            toast.error("Address and phone are required");
            return;
        }
        setCheckingOut(true);
        try {
            await api.post("/orders", {
                items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
                address, phone, notes,
            });
            toast.success("Order placed — we'll be in touch shortly");
            clear();
            setOpen(false);
            setStep("cart");
            setAddress(""); setPhone(""); setNotes("");
            navigate("/dashboard");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Order failed"));
        } finally {
            setCheckingOut(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-drawer">
            <div className="absolute inset-0 bg-[#3D0C1F]/45 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <aside className="relative w-full max-w-md bg-[#FBF6F0] border-l border-[#C29A5B]/30 grain flex flex-col h-full overflow-hidden">
                <div className="px-6 py-5 border-b border-[#6B1535]/15 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[#3D0C1F]">
                        <ShoppingBag size={18} className="text-[#8F622C]" />
                        <span className="font-serif-display text-2xl">{step === "cart" ? "Your Bag" : "Checkout"}</span>
                        <span className="text-[#7A5262] text-sm">({count})</span>
                    </div>
                    <button onClick={() => setOpen(false)} className="text-[#7A5262] hover:text-[#8F622C]" data-testid="cart-close"><X size={20} /></button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {items.length === 0 ? (
                        <div className="text-center text-[#7A5262] py-16">
                            <ShoppingBag size={32} className="mx-auto mb-4 text-[#8F622C]" />
                            <p>Your bag is empty.</p>
                            <button onClick={() => { setOpen(false); navigate("/shop"); }} className="btn-outline-gold mt-6">Browse Shop</button>
                        </div>
                    ) : step === "cart" ? (
                        <div className="space-y-4">
                            {items.map((i) => (
                                <div key={i.id} className="flex gap-4 border-b border-[#6B1535]/10 pb-4" data-testid={`cart-item-${i.id}`}>
                                    {i.image && <img src={i.image} alt={i.name} loading="lazy" decoding="async" className="w-20 h-20 object-cover" />}
                                    <div className="flex-1">
                                        <div className="flex justify-between gap-2">
                                            <h4 className="font-serif-display text-lg text-[#3D0C1F] leading-tight">{i.name}</h4>
                                            <button onClick={() => removeItem(i.id)} className="text-[#7A5262] hover:text-red-700 flex-shrink-0" data-testid={`remove-cart-${i.id}`}><Trash2 size={14} /></button>
                                        </div>
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="flex items-center border border-[#6B1535]/15">
                                                <button onClick={() => updateQty(i.id, i.quantity - 1)} className="p-2 hover:bg-[#6B1535]/5"><Minus size={12} className="text-[#8F622C]" /></button>
                                                <span className="px-3 text-sm text-[#3D0C1F]" data-testid={`qty-${i.id}`}>{i.quantity}</span>
                                                <button onClick={() => updateQty(i.id, i.quantity + 1)} className="p-2 hover:bg-[#6B1535]/5"><Plus size={12} className="text-[#8F622C]" /></button>
                                            </div>
                                            <div className="text-[#8F622C]">S${(i.price * i.quantity).toFixed(2)}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="checkout-form">
                            <div>
                                <label className="text-overline">Delivery Address</label>
                                <textarea required value={address} onChange={(e) => setAddress(e.target.value)} rows={3}
                                    placeholder="Street, unit, city, postal code"
                                    className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 text-[#3D0C1F] focus:border-[#C29A5B] outline-none"
                                    data-testid="checkout-address" />
                            </div>
                            <div>
                                <label className="text-overline">Phone</label>
                                <input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                                    placeholder="+65 ..."
                                    className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 text-[#3D0C1F] focus:border-[#C29A5B] outline-none"
                                    data-testid="checkout-phone" />
                            </div>
                            <div>
                                <label className="text-overline">Notes (optional)</label>
                                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                                    placeholder="Special instructions"
                                    className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 text-[#3D0C1F] focus:border-[#C29A5B] outline-none"
                                    data-testid="checkout-notes" />
                            </div>
                            <p className="text-xs text-[#8A6574] pt-3">Payment on delivery. We'll contact you to confirm and arrange delivery.</p>
                        </div>
                    )}
                </div>

                {items.length > 0 && (
                    <div className="border-t border-[#6B1535]/15 p-6 space-y-3">
                        <div className="flex justify-between">
                            <span className="text-overline">Total</span>
                            <span className="font-serif-display text-2xl gold-text">S${total.toFixed(2)}</span>
                        </div>
                        {step === "cart" ? (
                            <button onClick={() => setStep("checkout")} className="btn-gold w-full justify-center" data-testid="checkout-btn">Checkout</button>
                        ) : (
                            <div className="flex gap-2">
                                <button onClick={() => setStep("cart")} className="btn-outline-gold flex-1 justify-center" data-testid="checkout-back">Back</button>
                                <button onClick={placeOrder} disabled={checkingOut} className="btn-gold flex-1 justify-center disabled:opacity-50" data-testid="place-order-btn">
                                    {checkingOut ? "Placing…" : "Place Order"}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </aside>
        </div>
    );
}
